import { ChallengesList } from '@/components/challenges';
import {
  GroupEditDialog,
  GroupUnlockEditor,
  TemplateGroupCard,
} from '@/components/templates';
import { TemplateMapEditor } from '@/components/templates/TemplateMapEditor';
import { MapViewToggle, type MapView } from '@/components/shared/map/MapViewToggle';
import type { Challenge } from '@/config/objects/challenges';
import type { TemplateChallengeGroup, TemplateChallengeLink } from '@/config/objects/templates';
import { useTemplateContext } from '@/pages/templates/useTemplateContext';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Skeleton,
  toast,
  useApiClient,
  useApp,
} from '@startupquickstart/frontend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Layers, Lightbulb, Plus } from 'lucide-react';
import { useState } from 'react';
import { UngroupedChallengesList } from '@/components/templates/UngroupedChallengesList';

export function TemplateChallengesTab() {
  const { template, isLoading: templateLoading } = useTemplateContext();
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const { config } = useApp();

  const [view, setView] = useState<MapView>('list');
  const [showCreateGroupDialog, setShowCreateGroupDialog] = useState(false);
  const [editGroup, setEditGroup] = useState<TemplateChallengeGroup | null>(null);
  const [unlockEditorGroup, setUnlockEditorGroup] = useState<TemplateChallengeGroup | null>(null);
  const [addChallengeGroup, setAddChallengeGroup] = useState<TemplateChallengeGroup | null>(null);

  // Fetch groups
  const { data: groupsData, isLoading: groupsLoading } = useQuery<{
    success: boolean;
    data: TemplateChallengeGroup[];
  }>({
    queryKey: ['TemplateGroups', template?.id],
    queryFn: () => apiClient.get(`/templates/${template?.id}/groups`),
    enabled: !!template?.id,
  });

  // Fetch all challenges (including ungrouped)
  const { data: challengesData, isLoading: challengesLoading } = useQuery<{
    success: boolean;
    data: TemplateChallengeLink[];
  }>({
    queryKey: ['TemplateChallenges', template?.id],
    queryFn: () => apiClient.get(`/templates/${template?.id}/challenges`),
    enabled: !!template?.id,
  });

  const groups = groupsData?.data || [];
  const allChallenges = challengesData?.data || [];

  // Find ungrouped challenges (challenges without a groupId)
  const ungroupedChallenges = allChallenges.filter((c) => !c.groupId);

  // Flatten all challenges from all groups for the unlock editor
  const groupedChallenges = groups.flatMap((g) => g.challenges);

  // Add challenge to specific group mutation
  const addToGroupMutation = useMutation({
    mutationFn: async ({ challengeId, groupId }: { challengeId: string; groupId: string }) => {
      // First add the challenge to the template
      const result = (await apiClient.post(`/templates/${template?.id}/challenges`, {
        libraryChallengeId: challengeId,
      })) as { success: boolean; data?: { id: string } };

      // Then assign it to the group
      if (result.success && result.data?.id) {
        await apiClient.patch(`/templates/${template?.id}/challenges/${result.data.id}`, {
          groupId,
        });
      }

      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['TemplateGroups', template?.id] });
      queryClient.invalidateQueries({ queryKey: ['TemplateChallenges', template?.id] });
      toast.success('Challenge added to group');
      setAddChallengeGroup(null);
    },
    onError: (err: Error) => {
      toast.error(err?.message || 'Failed to add challenge');
    },
  });

  const handleAddChallengeToGroup = (challenge: Challenge) => {
    if (!addChallengeGroup) return;
    addToGroupMutation.mutate({
      challengeId: challenge.id,
      groupId: addChallengeGroup.id,
    });
  };

  const isLoading = templateLoading || groupsLoading || challengesLoading;

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Skeleton className="h-7 w-32" />
          <div className="flex gap-2">
            <Skeleton className="h-9 w-28" />
            <Skeleton className="h-9 w-32" />
          </div>
        </div>
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-48 rounded-xl" />
        ))}
      </div>
    );
  }

  if (!template) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Template not found</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Lightbulb className="h-5 w-5 text-muted-foreground" />
          <h2 className="text-lg font-semibold text-foreground">Challenges</h2>
          <span className="text-sm text-muted-foreground">
            ({allChallenges.length} total, {groups.length} group{groups.length !== 1 ? 's' : ''})
          </span>
        </div>
        <div className="flex items-center gap-2">
          <MapViewToggle view={view} onViewChange={setView} />
          <Button onClick={() => setShowCreateGroupDialog(true)} size="sm" icon={Layers}>
            Add Group
          </Button>
        </div>
      </div>

      {/* Map view — every template challenge at its resolved (override ?? library) pin */}
      {view === 'map' && (
        <TemplateMapEditor
          templateId={template.id}
          groups={groups}
          challenges={allChallenges}
          googleMapsApiKey={config?.googleMapsApiKey}
          editable
        />
      )}

      {/* Empty state - no groups */}
      {view === 'list' && groups.length === 0 && (
        <div className="text-center py-12 px-4 border-2 border-dashed rounded-xl">
          <Layers className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
          <h3 className="text-lg font-medium mb-2">No challenge groups yet</h3>
          <p className="text-muted-foreground mb-4">
            Create groups to organize your challenges
          </p>
          <Button onClick={() => setShowCreateGroupDialog(true)} icon={Layers}>
            Create First Group
          </Button>
        </div>
      )}

      {/* Groups list */}
      {view === 'list' && groups.length > 0 && (
        <div className="space-y-0">
          {groups.map((group, index) => (
            <TemplateGroupCard
              key={group.id}
              templateId={template.id}
              group={group}
              allGroups={groups}
              allChallenges={groupedChallenges}
              onEdit={() => setEditGroup(group)}
              onEditUnlock={() => setUnlockEditorGroup(group)}
              onAddChallenge={() => setAddChallengeGroup(group)}
              isFirst={index === 0}
              isLast={index === groups.length - 1}
            />
          ))}

          {/* Add new group button at bottom */}
          <div className="flex justify-center pt-2">
            <Button
              variant="outline"
              onClick={() => setShowCreateGroupDialog(true)}
              className="border-dashed"
              icon={Plus}
            >
              Add New Group
            </Button>
          </div>
        </div>
      )}

      {/* Ungrouped challenges section */}
      {view === 'list' && ungroupedChallenges.length > 0 && (
        <UngroupedChallengesList
          templateId={template.id}
          challenges={ungroupedChallenges}
          groups={groups}
        />
      )}

      {/* Create/Edit group dialog */}
      <GroupEditDialog
        templateId={template.id}
        group={editGroup}
        open={showCreateGroupDialog || !!editGroup}
        onOpenChange={(open) => {
          if (!open) {
            setShowCreateGroupDialog(false);
            setEditGroup(null);
          }
        }}
      />

      {/* Unlock condition editor */}
      {unlockEditorGroup && (
        <GroupUnlockEditor
          templateId={template.id}
          group={unlockEditorGroup}
          allGroups={groups}
          allChallenges={groupedChallenges}
          open={!!unlockEditorGroup}
          onOpenChange={(open) => !open && setUnlockEditorGroup(null)}
        />
      )}

      {/* Add challenge to specific group dialog */}
      <Dialog open={!!addChallengeGroup} onOpenChange={(open) => !open && setAddChallengeGroup(null)}>
        <DialogContent className="!w-[90vw] !max-w-7xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Add Challenge to "{addChallengeGroup?.name}"</DialogTitle>
            <DialogDescription>Select a challenge from the library to add</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-auto -mx-6 px-6">
            <ChallengesList
              onItemClick={handleAddChallengeToGroup}
              className="[&_.crud-list-header]:hidden"
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
